import { observable, reaction, toJS } from 'mobx'

export enum BetaFeature {
  NewEtusivu = 'newEtusivu',
}

export type BetaFeatureMeta = {
  key: BetaFeature
  title: string
  affectedRoutes?: string[]
}

export const BETA_FEATURES_META: Record<BetaFeature, BetaFeatureMeta> = {
  [BetaFeature.NewEtusivu]: {
    key: BetaFeature.NewEtusivu,
    title: 'new-frontpage',
    affectedRoutes: ['/'],
  },
}

/**
 * Features here are always enabled for everyone.
 * Remove a feature from this list to make it opt-in again.
 */
export const ALWAYS_ON: BetaFeature[] = [BetaFeature.NewEtusivu]

const STORAGE_KEY = 'betaStore.enabledFeatures'

const loadFromStorage = (): BetaFeature[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const valid = new Set(Object.values(BetaFeature))
    return parsed.filter((f): f is BetaFeature => valid.has(f))
  } catch {
    return []
  }
}

const createBetaStore = () => {
  const state = observable({
    enabledFeatures: new Set<BetaFeature>(loadFromStorage()),
  })

  const isEnabled = (feature: BetaFeature): boolean => {
    if (ALWAYS_ON.includes(feature)) {
      return true
    }
    return state.enabledFeatures.has(feature)
  }

  const enable = (feature: BetaFeature): void => {
    state.enabledFeatures.add(feature)
  }

  const disable = (feature: BetaFeature): void => {
    state.enabledFeatures.delete(feature)
  }

  const toggle = (feature: BetaFeature, enabled: boolean): void => {
    if (enabled) enable(feature)
    else disable(feature)
  }

  const reset = (): void => {
    state.enabledFeatures.clear()
  }

  // Persist to localStorage whenever the set changes
  reaction(
    () => Array.from(state.enabledFeatures),
    (features) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toJS(features)))
      } catch {
        // localStorage unavailable (private mode, quota, SSR) — silently ignore
      }
    },
  )

  return {
    // Expose state as a getter so observers track it correctly
    get enabledFeatures() {
      return state.enabledFeatures
    },
    isEnabled,
    enable,
    disable,
    toggle,
    reset,
  }
}

const betaStore = createBetaStore()
export default betaStore

export type BetaStore = ReturnType<typeof createBetaStore>
